function SimulatorRunnerController(CommonService, stateService, FileSaver, __env, $interval, $timeout, $anchorScroll, $location, toasterService) {
  const ctrl = this;
  const preLoadStream = CommonService.preLoadStream;

  ctrl.$onInit = () => {
    ctrl.activeStop = null;

    ctrl.blMode = __env.operator === 'bakerloo';

    if (ctrl.simulationOptions.index > 0) {
      // User selected current station on previous screen
      ctrl.goToStop(ctrl.simulationOptions.index, ctrl.simulationOptions.index);
    }
  };

  ctrl.$onChanges = changes => {
    if (ctrl.simulationData) {
      ctrl.nextPos = 0;
      ctrl.workspaceId = stateService.getSelectedWorkspace().id;
      ctrl.stops = _.filter(ctrl.simulationData.simulator_reports, m => m.train.atStop);
      ctrl.simulationEvents = _.filter(ctrl.simulationData.simulator_reports, m => m.streams.length > 0);
      ctrl.stations = ctrl.simulationData.logic_packages.length > 0 && ctrl.simulationData.logic_packages[0].stations;
      ctrl.statistics = ctrl.simulationData.statistics;

      findStationLocationAndName();

      ctrl.currentTime = 0;
      ctrl.index = 0;
      ctrl.highlightIndex = 0;
      ctrl.streamHighlightIndex = 0;

      ctrl.simStartDateMillis = DateTime.fromFormat(ctrl.simulationOptions.date, 'MM/dd/yyyy h:mm:ss a', {zone: 'utc'}).toMillis();
      ctrl.audioList = buildAudioList(ctrl.simulationEvents);
      if (ctrl.audioList) {
        loadStreamAudio(ctrl.audioList);
      }
      if (ctrl.stops.length > 0) ctrl.goToStop(ctrl.stops[0], 0);
    }
  };

  ctrl.$onDestroy = () => {
    $interval.cancel(ctrl.playPromise);
    $interval.cancel(ctrl.runnerPromise);
    $timeout.cancel(ctrl.highlightPromise);
  };

  // Loop through the run stations list and calculate the locations/positions for the stations using
  // ctrl.stations list which has all the stations in the database.
  // Position needs to be calculated from between_var.var.DISTANCE which is the distance to the next station
  const findStationLocationAndName = () => {
    ctrl.simulationOptions.run.stations.forEach((el, index) => {
      if (index === ctrl.simulationOptions.run.stations.length - 1) {
        el.position = ctrl.nextPos;
      } else {
        for (const sta of Object.values(ctrl.stations)) {
          if (el.id === sta.id) {
            for (const bet of Object.values(sta.between_var)) {
              if (bet.id === ctrl.simulationOptions.run.stations[index + 1].id) {
                if (ctrl.nextPos !== 0) {
                  el.position = ctrl.nextPos;
                } else {
                  el.position = 0;
                }
                ctrl.nextPos += bet.var.DISTANCE;
              }
            }
          }
        }
      }
    });
    ctrl.stops.forEach(s => {
      for (const sta of Object.values(ctrl.simulationOptions.run.stations)) {
        if (sta.position + 2 >= s.train.position && sta.position - 2 <= s.train.position) s.name = sta.name;
      }
    });
  };

  const buildAudioList = simulationEvents => simulationEvents.map(sim => {
    return {
      time: sim.train.time,
      date: new Date(ctrl.simStartDateMillis + sim.train.time * 1000),
      streams: sim.streams
    };
  });

  const loadStreamAudio = audioList => {
    _.each(audioList, item => {
      _.each(item.streams, stream => {
        // Stream has 'types' array where the first element is always TEXT stream and the second element is AUDIO
        // except if there is no TEXT stream in which case AUDIO is naturally the first and only element.
        // Only try to load the audio file if the AUDIO stream doesn't have 'cancelled' status
        if (stream.stream_id >= 0 && (stream.types.length === 2 && !stream.types[1].cancelled ||
          stream.types.length === 1 && !stream.types[0].cancelled)) {
          let macroList = stream.macro_fragments.map(macro => {
            return {
              name: macro.macro_value.name,
              id: macro.macro_value.id
            };
          });
          // Use preLoadStream from CommonService which fetches the audio file and returns the blob
          preLoadStream(ctrl.workspaceId, { id: stream.stream_id }, macroList).then(data => {
            stream.streamFileUrl = data;
          });
        }
      });
    });
  };

  // ctrl.distanceStep is the seed where the stepping is started. It changes when user moves around the timeline
  const distanceRunner = reports => {
    const distanceObject = reports[ctrl.distanceStep].engine.engVar.find(el => el.name === 'DISTANCE');
    if (distanceObject) {
      ctrl.currentDistance = distanceObject.value;
    }
    ctrl.distanceStep += 1;
  };

  // ctrl.velocityStep is the seed where the stepping is started. It changes when user moves around the timeline
  const velocityRunner = reports => {
    const speedObject = reports[ctrl.velocityStep].engine.envVar.find(el => el.name === 'SPEED');
    if (speedObject) {
      ctrl.currentSpeed = speedObject.value;
    }
    ctrl.velocityStep += 1;
  };

  ctrl.goToStop = (stop, stationIndex) => {
    ctrl.activeStation = stationIndex;
    ctrl.activeStop = stop;
    ctrl.activeConnector = null;
    ctrl.currentTime = stop.time > 0 ? stop.time - 10 : stop.time;
    ctrl.currentDate = new Date(ctrl.simStartDateMillis + ctrl.currentTime * 1000);
    ctrl.index = ctrl.audioList.findIndex(event => event.time > ctrl.currentTime);
    ctrl.highlightIndex = ctrl.index;
    ctrl.streamHighlightIndex = 0;
    setRunners();
    ctrl.scrollToElement(ctrl.highlightIndex);
    ctrl.noAnnouncements = ctrl.index === -1;
    ctrl.lastAudioIndex = checkLastIndex(ctrl.audioList, ctrl.index);
  };

  ctrl.scrollToElement = x => {
    const newHash = `anchor${x}`;
    if ($location.hash() !== newHash) {
      $location.hash(`anchor${x}`); // $anchorScroll will scroll to this new hash
    } else {
      $anchorScroll(); // hash haven't changed
    }
  };

  ctrl.pause = () => {
    $interval.cancel(ctrl.playPromise);
    $interval.cancel(ctrl.runnerPromise);
    $timeout.cancel(ctrl.highlightPromise);
    ctrl.playing = false;
  };

  ctrl.clear = () => {
    ctrl.pause();
    if (ctrl.audio) ctrl.audio.pause(); // Acts as 'stop' here
    ctrl.streamTextActive = false;
    ctrl.currentDistance = null;
    ctrl.currentSpeed = null;
    ctrl.endReached = false;
    if (ctrl.stops.length > 0) ctrl.goToStop(ctrl.stops[0], 0);
  };

  ctrl.playAndPause = () => {
    if (ctrl.playing) {
      ctrl.pause();
    } else {
      const lastCall = _.last(ctrl.audioList).time;
      ctrl.playing = true;
      setRunners();
      ctrl.playPromise = $interval(() => {
        const roundedValue = Math.round((ctrl.currentTime += 0.1) * 10) / 10;
        ctrl.currentTime = roundedValue;
        ctrl.currentDate = new Date(ctrl.simStartDateMillis + roundedValue * 1000);
        if (ctrl.currentTime >= lastCall) {
          ctrl.pause();
          ctrl.endReached = true;
        } else {
          ctrl.endReached = false;
        }
        checkList(ctrl.currentTime);
      }, 100);
      ctrl.runnerPromise = $interval(() => {
        distanceRunner(ctrl.simulationData.simulator_reports);
        velocityRunner(ctrl.simulationData.simulator_reports);
        if (ctrl.activeStop && ctrl.currentTime >= ctrl.activeStop.time + 13 && ctrl.activeStation !== undefined) {
          ctrl.activeConnector = ctrl.activeStation;
          ctrl.activeStation = undefined;
        }
        const stopIndex = findStopIndex(ctrl.stops, ctrl.currentTime);
        const stop = findStop(ctrl.stops, ctrl.currentTime);
        if (stopIndex !== -1 && !ctrl.activeStation) {
          ctrl.activeStation = stopIndex;
          ctrl.activeStop = stop;
          ctrl.activeConnector = null;
        }
      }, 1000);
    }
  };

  ctrl.goToAnnouncement = index => {
    ctrl.streamTextActive = false;
    ctrl.index = index;
    ctrl.highlightIndex = index;
    ctrl.streamHighlightIndex = 0;
    ctrl.scrollToElement(ctrl.highlightIndex);
    ctrl.lastAudioIndex = checkLastIndex(ctrl.audioList, ctrl.index);
    if (ctrl.audioList.length > ctrl.index) {
      ctrl.currentTime = ctrl.audioList[ctrl.index].time - 3;
      ctrl.currentDate = new Date(ctrl.simStartDateMillis + ctrl.currentTime * 1000);
      setRunners();
    }
    for (let i = 0; i < ctrl.stops.length; ++i) {
      if (ctrl.stops[i].time - 10 <= ctrl.currentTime && ctrl.currentTime <= ctrl.stops[i].time + 10) {
        ctrl.activeConnector = undefined;
        ctrl.activeStation = i;
      } else if (ctrl.stops[i].time < ctrl.currentTime && ctrl.currentTime < ctrl.stops[i + 1].time) {
        ctrl.activeConnector = i;
        ctrl.activeStation = undefined;
      }
    }
  };

  ctrl.next = () => {
    if (ctrl.audioList[ctrl.index].time - ctrl.currentTime <= 3) {
      ctrl.index += 1;
    }
    ctrl.goToAnnouncement(ctrl.index);
  };

  // Go back to the previous announcement
  ctrl.previous = () => {
    if (ctrl.index !== 0) {
      ctrl.index -= 1;
      ctrl.goToAnnouncement(ctrl.index);
    } else {
      ctrl.clear();
    }
  };

  ctrl.showStats = () => {
    ctrl.statsVisible = !ctrl.statsVisible;
  };

  ctrl.downloadTranscript = () => {
    const TIMESTAMP_FORMAT = "yyyy-MM-dd HH:mm:ss";

    // Convert a date string to a common format without adjusting the timezone
    const startTimestamp = DateTime.fromFormat(ctrl.simulationOptions.date, "MM/dd/yyyy h:mm:ss a", {zone: 'utc'}).toFormat(TIMESTAMP_FORMAT);

    // Always start with header
    let textData = "PTI " + ctrl.simulationOptions.trainNumber.number + ": " + ctrl.simulationOptions.run.name + "\n" +
      "at " + startTimestamp + "\n" +
      '='.repeat(25) + "\n";

    // Find station names & times during the simulation
    const stations = ctrl.simulationData.simulator_reports
      .filter(rep => rep.name)
      .map(rep => ({ name: rep.name, time: rep.time }));

    // Merge stations and announcements in timestamp order, preferring stations on equal times.
    let audioListIndex = 0;
    let stationIndex = 0;

    while (stationIndex < stations.length || audioListIndex < ctrl.audioList.length) {
      const currentStation = stations[stationIndex];
      const currentAnnouncement = ctrl.audioList[audioListIndex];
      const stationTime = currentStation ? currentStation.time : Number.POSITIVE_INFINITY;
      const announcementTime = currentAnnouncement ? currentAnnouncement.time : Number.POSITIVE_INFINITY;

      if (stationTime <= announcementTime) {
        textData += "\n" + currentStation.name + "\n";
        stationIndex++;
        continue;
      }

      currentAnnouncement.streams.forEach(stream => {
        // Convert a date/time values to a common format without adjusting the timezone
        const timestamp = DateTime.fromJSDate(currentAnnouncement.date, {zone: 'utc'}).toFormat(TIMESTAMP_FORMAT);
        textData += "\n  " + timestamp + "\n  " + stream.name + " (stream id " + stream.stream_id + ")\n";

        stream.types.forEach(type => {
          textData += "    - " + type.type + ": ";
          if (type.type === "TEXT") {
            textData += type.text + " (segment id " + parseSegmentId(type.fragments[0].filename) + ")\n";
          } else if (type.type === "AUDIO") {
            let optionalMultipartDelimiter = "";
            if (type.fragments.length > 1) {
              // Line break & indentation if the fragment consists of more than one audio segment
              textData += "[multipart]\n";
              optionalMultipartDelimiter = ' '.repeat(13);
            }
            type.fragments.forEach(fragment => {
              textData += optionalMultipartDelimiter + fragment.filename + " (duration " + fragment.duration + " s) (segment id " + parseSegmentId(fragment.filename) + ")\n";
            });
          } else {
            // Should never be here, just to find out if any errors in logic
            textData += "ERROR!\n";
          }

          // Notify overlapping announcements
          if (type.interrupted) {
            textData += "      ### INTERRUPTED: " + type.interruptedAt + "\n";
          }
        });
      });
      audioListIndex++;
    }

    let data = new Blob([textData], { type: 'text/plain' });
    let filename =  "simulation-data.txt";
    FileSaver.saveAs(data, filename);
  };

  ctrl.downloadSimulationLogs = () => {
    stateService.getSimulationLogs().then(response => {
      if (response.statusCode !== 200) {
        toasterService.error('toaster.file-download-failure', ' Status code:  ' +  response.status);
      } else {
        toasterService.success('toaster.file-download-success', ' ');
        FileSaver.saveAs(response.response, 'simulation-logs.zip');
      }
    });
  };

  ctrl.shouldBeInStreamList = (index) => {
    if (ctrl.audioList[ctrl.highlightIndex].streams[index].types[0].type !== 'TEXT') {
      return false;
    }
    const streamListLength = ctrl.audioList[ctrl.highlightIndex].streams.length;
    // First/last stream text is highlighted so need to include next/previous 2 texts
    if (ctrl.streamHighlightIndex === 0 || ctrl.streamHighlightIndex === streamListLength - 1) {
      return Math.abs(ctrl.streamHighlightIndex - index) <= 2;
    }
    return Math.abs(ctrl.streamHighlightIndex - index) <= 1;
  };

  const parseSegmentId = (filename) => {
    // Parse segment number from the filename by removing all the other characters
    return filename.match(/\d+/);
  };

  const checkLastIndex = (audioList, index) => index === audioList.length - 1 ? true : false;
  const findStopIndex = (stops, time) => stops.findIndex(s => (s.time > time - 10 && s.time < time + 10));
  const findStop = (stops, time) => stops.find(s => (s.time > time - 10 && s.time < time + 10));
  
  const setRunners = () => {
    const reportIndex = ctrl.simulationData.simulator_reports.findIndex(el => el.time === ctrl.currentTime);
    if (reportIndex !== -1) {
      ctrl.distanceStep = reportIndex;
      ctrl.velocityStep = reportIndex;
    } else {
      ctrl.distanceStep = 0;
      ctrl.velocityStep = 0;
    }
  };

  const checkList = time => {
    if (ctrl.audioList.length === 0 || ctrl.index >= ctrl.audioList.length || ctrl.index === -1) return;
    if (ctrl.audioList[ctrl.index].time - time === 6) {
      ctrl.scrollToElement(ctrl.index);
      const stopIndex = findStopIndex(ctrl.stops, ctrl.currentTime);
      const stop = findStop(ctrl.stops, ctrl.currentTime);
      if (stopIndex !== -1 && !ctrl.activeStation) {
        ctrl.activeStation = stopIndex;
        ctrl.activeStop = stop;
        ctrl.activeConnector = null;
      }
    }
    if (ctrl.audioList[ctrl.index].time === time) {
      console.log(`Play audio for timestamp: ${time}`);
      playAudio(ctrl.audioList[ctrl.index].streams);
      ctrl.index += 1;
    }
  };

  const playAudio = streams => {
    let totalDuration = 0;
    streams.forEach(stream => {
      // If stream doesn't have streamFileUrl or if it's cancelled, don't play the audio
      if (!stream.streamFileUrl) { return; }
      let textDuration = 0;
      let audioDuration = 0;
      if (stream.types.length === 2 && !stream.types[1].cancelled) {
        textDuration = stream.types[0].duration;
        audioDuration = stream.types[1].duration;
        $timeout(() => {
          highlightStreamText(textDuration, audioDuration);
        }, totalDuration);
        $timeout(() => {
          playStream(stream);
        }, totalDuration);
      } else if (stream.types.length === 1 && !stream.types[0].cancelled) {
        if (stream.types[0].type === 'TEXT') {
          textDuration = stream.types[0].duration;
          $timeout(() => {
            highlightStreamText(textDuration, audioDuration);
          }, totalDuration);
        } else if (stream.types[0].type === 'AUDIO') {
          audioDuration = stream.types[0].duration;
          $timeout(() => {
            playStream(stream);
          }, totalDuration);
        }
      }
      // Add offset of 100ms to prevent race condition
      const announcementDuration = Math.max(textDuration, audioDuration) * 1000 + 100;
      totalDuration += announcementDuration;
    });

    ctrl.highlightPromise = $timeout(() => {
      ctrl.highlightIndex += 1;
      ctrl.streamHighlightIndex = 0;
      ctrl.scrollToElement(ctrl.highlightIndex);
    }, totalDuration);
  };

  const highlightStreamText = (textDuration, audioDuration) => {
    ctrl.streamTextActive = true;
    $timeout(() => {
      ctrl.streamTextActive = false;
    }, textDuration * 1000);
    $timeout(() => {
      ctrl.streamHighlightIndex++;
    }, Math.max(textDuration, audioDuration) * 1000);
  };

  const playStream = stream => {
    ctrl.audio = new Audio();
    ctrl.audio.src = stream.streamFileUrl;
    ctrl.audio.volume = 0.6;
    ctrl.audio.play();
    let audioTypeIndex = 1;
    if (stream.types[0].type === 'AUDIO') {
      audioTypeIndex = 0;
      $timeout(() => {
        ctrl.streamHighlightIndex++;
      }, stream.types[audioTypeIndex].duration * 1000);
    }
    console.log(`${ctrl.currentTime} Play audio for stream ${stream.stream_id}.`);
    // If stream has interrupted: true, then stream playback must be interrupted when specified in 'interruptedAt'
    if (stream.types[audioTypeIndex].interrupted) {
      $timeout(() => {
        ctrl.audio.pause(); // Acts as 'stop' here
        console.log(`Stream ${stream.stream_id} interrupted after ${stream.types[audioTypeIndex].interruptedAt} seconds.`);
      }, stream.types[audioTypeIndex].interruptedAt * 1000);
    }
  };
}

angular
  .module('pis-tool.module')
  .controller('SimulatorRunnerController', SimulatorRunnerController);
